import { useState } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, ChevronRight, Italic, Link2 } from "lucide-react";
import { cleanPaste } from "@/lib/richtext";

const OFF = { heading: false, blockquote: false, bulletList: false, orderedList: false, listItem: false, codeBlock: false, code: false, horizontalRule: false, strike: false, underline: false };

const Mini = ({ editor }) => {
  const s = useEditorState({ editor, selector: ({ editor: e }) => ({ b: e.isActive("bold"), i: e.isActive("italic"), a: e.isActive("link") }) });
  const link = () => {
    if (s.a) return editor.chain().focus().unsetLink().run();
    const href = window.prompt("Link address (https://…)");
    if (href) editor.chain().focus().setLink({ href: /^https?:\/\//.test(href) ? href : `https://${href}` }).run();
  };
  const btn = (on, onClick, Icon, id) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={`note-${id}`}
      className={`h-7 w-7 rounded flex items-center justify-center ${on ? "bg-amber-500/15 text-amber-300" : "text-slate-500 hover:text-slate-200"}`}><Icon className="w-3.5 h-3.5" /></button>
  );
  return <div className="flex gap-0.5">{btn(s.b, () => editor.chain().focus().toggleBold().run(), Bold, "bold")}{btn(s.i, () => editor.chain().focus().toggleItalic().run(), Italic, "italic")}{btn(s.a, link, Link2, "link")}</div>;
};

// Collapsible beginning/end note for a chapter; not part of the story word count
export const NoteBox = ({ label, value, onChange, testId }) => {
  const [open, setOpen] = useState(!!value);
  const editor = useEditor({
    extensions: [StarterKit.configure({ ...OFF, link: { openOnClick: false, autolink: true, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } } })],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: { attributes: { class: "note-prose min-h-[3.5rem] focus:outline-none", "data-testid": `${testId}-editor` }, transformPastedHTML: cleanPaste },
  });
  return (
    <div className="w-full px-6 sm:px-10 lg:px-16" data-testid={testId}>
      <div className="ml-4 rounded-xl border border-dashed border-line bg-veil/[0.015]">
        <div className="flex items-center gap-2 px-3 py-1.5">
          <button type="button" onClick={() => setOpen(!open)} className="flex flex-1 items-center gap-1.5 text-left text-xs italic text-slate-400 hover:text-amber-200" data-testid={`${testId}-toggle`}>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />{label}{!open && value && <span className="not-italic text-[10px] text-amber-500/70 ml-1">· has a note</span>}
          </button>
          {open && editor && <Mini editor={editor} />}
        </div>
        {open && <div className="px-4 pb-3"><EditorContent editor={editor} /></div>}
      </div>
    </div>
  );
};
